import React, { useState, useMemo } from 'react';
import ShowcasePageWrapper from '../shared/ShowcasePageWrapper';
import { generateWizardData } from '../shared/SampleData';
import Button from '@salesforce/design-system-react/components/button';
import Icon from '@salesforce/design-system-react/components/icon';
import Input from '@salesforce/design-system-react/components/input';
import Textarea from '@salesforce/design-system-react/components/textarea';
import Checkbox from '@salesforce/design-system-react/components/checkbox';
import Radio from '@salesforce/design-system-react/components/radio';
import RadioGroup from '@salesforce/design-system-react/components/radio-group';
import Combobox from '@salesforce/design-system-react/components/combobox';
import ProgressIndicator from '@salesforce/design-system-react/components/progress-indicator';
import Tabs from '@salesforce/design-system-react/components/tabs';
import TabsPanel from '@salesforce/design-system-react/components/tabs/panel';
import ExpandableSection from '@salesforce/design-system-react/components/expandable-section';
import Badge from '@salesforce/design-system-react/components/badge';
import Toast from '@salesforce/design-system-react/components/toast';
import ToastContainer from '@salesforce/design-system-react/components/toast/container';

const cardStyle = {
  backgroundColor: 'white',
  border: '1px solid #dddbda',
  borderRadius: '4px',
  padding: '20px',
};

const sectionSpacing = { marginBottom: '24px' };

const steps = [
  { id: 0, label: 'Account', assistiveText: 'Account Lookup' },
  { id: 1, label: 'Configure', assistiveText: 'Configuration' },
  { id: 2, label: 'Products', assistiveText: 'Product Selection' },
  { id: 3, label: 'Review', assistiveText: 'Review' },
  { id: 4, label: 'Confirm', assistiveText: 'Confirmation' },
];

const regionOptions = [
  { id: 'us-east', label: 'US East' },
  { id: 'us-west', label: 'US West' },
  { id: 'eu-west', label: 'EU West' },
  { id: 'eu-central', label: 'EU Central' },
  { id: 'ap-southeast', label: 'Asia Pacific SE' },
  { id: 'ap-northeast', label: 'Asia Pacific NE' },
];

const stateOptions = [
  { id: 'CA', label: 'California' },
  { id: 'NY', label: 'New York' },
  { id: 'TX', label: 'Texas' },
  { id: 'FL', label: 'Florida' },
  { id: 'IL', label: 'Illinois' },
  { id: 'WA', label: 'Washington' },
  { id: 'MA', label: 'Massachusetts' },
  { id: 'CO', label: 'Colorado' },
];

const salutationOptions = [
  { id: 'mr', label: 'Mr.' },
  { id: 'ms', label: 'Ms.' },
  { id: 'mrs', label: 'Mrs.' },
  { id: 'dr', label: 'Dr.' },
  { id: 'prof', label: 'Prof.' },
];

const mockUploadedFiles = [
  { name: 'contract_2025.pdf', size: '2.4 MB', type: 'pdf', date: '2025-07-15' },
  { name: 'requirements_spec.docx', size: '1.1 MB', type: 'doc', date: '2025-07-14' },
  { name: 'architecture_diagram.png', size: '856 KB', type: 'image', date: '2025-07-12' },
  { name: 'budget_breakdown.xlsx', size: '345 KB', type: 'spreadsheet', date: '2025-07-10' },
];

// ============================================================
// Multi-Step Wizard Tab
// ============================================================
const MultiStepWizard = () => {
  const wizardData = useMemo(() => generateWizardData(), []);

  const [currentStep, setCurrentStep] = useState(0);
  const [accountSearch, setAccountSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [configType, setConfigType] = useState('standard');
  const [customDomain, setCustomDomain] = useState('');
  const [apiLimit, setApiLimit] = useState('');
  const [selectedRegion, setSelectedRegion] = useState([]);
  const [enableNotifications, setEnableNotifications] = useState(true);
  const [enableAutoSync, setEnableAutoSync] = useState(false);
  const [enableReporting, setEnableReporting] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [orderRef, setOrderRef] = useState('');

  const filteredAccounts = useMemo(() => {
    if (!accountSearch.trim()) return wizardData.accounts;
    const lower = accountSearch.toLowerCase();
    return wizardData.accounts.filter(
      (a) =>
        a.name.toLowerCase().includes(lower) ||
        a.industry.toLowerCase().includes(lower) ||
        a.type.toLowerCase().includes(lower)
    );
  }, [accountSearch, wizardData.accounts]);

  const toggleProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const selectedProductDetails = useMemo(() => {
    return wizardData.products.filter((p) => selectedProducts.includes(p.id));
  }, [selectedProducts, wizardData.products]);

  const totalPrice = useMemo(() => {
    return selectedProductDetails.reduce((sum, p) => sum + p.price, 0);
  }, [selectedProductDetails]);

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedAccount !== null;
      case 1:
        return selectedRegion.length > 0;
      case 2:
        return selectedProducts.length > 0;
      case 3:
        return agreedToTerms;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (currentStep === 3) {
      const ref = `ORD-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
      setOrderRef(ref);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 5000);
    }
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAccountSearch('');
    setSelectedAccount(null);
    setConfigType('standard');
    setCustomDomain('');
    setApiLimit('');
    setSelectedRegion([]);
    setEnableNotifications(true);
    setEnableAutoSync(false);
    setEnableReporting(true);
    setSelectedProducts([]);
    setAgreedToTerms(false);
    setShowToast(false);
    setOrderRef('');
  };

  const goToStep = (stepId) => {
    if (stepId < currentStep) {
      setCurrentStep(stepId);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Software': return '#0176d3';
      case 'Services': return '#2e844a';
      case 'Hardware': return '#fe9339';
      default: return '#706e6b';
    }
  };

  // Step 1: Account Lookup
  const renderAccountLookup = () => (
    <div>
      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#080707', marginBottom: '8px' }}>
        Account Lookup
      </h3>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '20px' }}>
        Search for an existing account to associate with this order.
      </p>
      <div style={{ marginBottom: '20px' }}>
        <Input
          label="Search Accounts"
          placeholder="Search by name, industry, or type..."
          value={accountSearch}
          onChange={(e, { value }) => setAccountSearch(value)}
          iconLeft={
            <Icon
              category="utility"
              name="search"
              size="x-small"
              className="slds-input__icon slds-input__icon_left"
            />
          }
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
        {filteredAccounts.map((account) => {
          const isSelected = selectedAccount && selectedAccount.id === account.id;
          return (
            <div
              key={account.id}
              onClick={() => setSelectedAccount(account)}
              style={{
                ...cardStyle,
                padding: '16px',
                cursor: 'pointer',
                borderColor: isSelected ? '#0176d3' : '#dddbda',
                borderWidth: isSelected ? '2px' : '1px',
                backgroundColor: isSelected ? '#f0f7ff' : 'white',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.borderColor = '#b0c4de';
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.borderColor = '#dddbda';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#080707' }}>
                  {account.name}
                </span>
                {isSelected && (
                  <Icon category="utility" name="check" size="x-small" style={{ fill: '#0176d3' }} />
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Badge
                  content={account.industry}
                  style={{ backgroundColor: '#f3f3f3', color: '#706e6b' }}
                />
                <Badge
                  content={account.type}
                  style={{
                    backgroundColor: account.type === 'Customer' ? '#ebf7e6' : account.type === 'Partner' ? '#eef4ff' : '#fef3e8',
                    color: account.type === 'Customer' ? '#2e844a' : account.type === 'Partner' ? '#0176d3' : '#fe9339',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      {filteredAccounts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#706e6b' }}>
          <Icon category="utility" name="search" size="large" style={{ fill: '#dddbda', marginBottom: '12px' }} />
          <p>No accounts found matching "{accountSearch}"</p>
        </div>
      )}
    </div>
  );

  // Step 2: Configuration
  const renderConfiguration = () => (
    <div>
      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#080707', marginBottom: '8px' }}>
        Configuration
      </h3>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '24px' }}>
        Choose configuration options for the selected account.
      </p>

      <div style={{ marginBottom: '24px' }}>
        <RadioGroup
          labels={{ label: 'Configuration Type' }}
          onChange={(e) => setConfigType(e.target.value)}
          variant="list"
        >
          <Radio
            label="Standard"
            value="standard"
            checked={configType === 'standard'}
            variant="base"
          />
          <Radio
            label="Custom"
            value="custom"
            checked={configType === 'custom'}
            variant="base"
          />
          <Radio
            label="Enterprise"
            value="enterprise"
            checked={configType === 'enterprise'}
            variant="base"
          />
        </RadioGroup>
      </div>

      {(configType === 'custom' || configType === 'enterprise') && (
        <div style={{
          backgroundColor: '#f0f7ff',
          border: '1px solid #b0c4de',
          borderRadius: '4px',
          padding: '20px',
          marginBottom: '24px',
        }}>
          <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#0176d3', marginBottom: '16px' }}>
            {configType === 'enterprise' ? 'Enterprise' : 'Custom'} Configuration Options
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Input
              label="Custom Domain"
              placeholder="e.g., app.yourdomain.com"
              value={customDomain}
              onChange={(e, { value }) => setCustomDomain(value)}
            />
            <Input
              label="API Rate Limit (requests/hour)"
              placeholder={configType === 'enterprise' ? '50000' : '10000'}
              type="number"
              value={apiLimit}
              onChange={(e, { value }) => setApiLimit(value)}
            />
          </div>
          {configType === 'enterprise' && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: '#706e6b' }}>
              <Icon category="utility" name="info" size="xx-small" style={{ fill: '#706e6b', marginRight: '6px' }} />
              Enterprise plans include dedicated support, custom SLA, and unlimited API calls.
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: '24px' }}>
        <Combobox
          labels={{ label: 'Region', placeholder: 'Select a region...' }}
          options={regionOptions.map((r) => ({ ...r, value: r.id }))}
          selection={selectedRegion}
          onSelect={(e, data) => setSelectedRegion(data.selection)}
          variant="readonly"
          singleInputDisabled={false}
        />
      </div>

      <div style={{
        backgroundColor: '#f3f3f3',
        borderRadius: '4px',
        padding: '20px',
      }}>
        <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#080707', marginBottom: '16px' }}>
          Feature Toggles
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Checkbox
            labels={{ label: 'Enable Notifications' }}
            checked={enableNotifications}
            onChange={(e, { checked }) => setEnableNotifications(checked)}
          />
          <Checkbox
            labels={{ label: 'Enable Auto-Sync' }}
            checked={enableAutoSync}
            onChange={(e, { checked }) => setEnableAutoSync(checked)}
          />
          <Checkbox
            labels={{ label: 'Enable Reporting' }}
            checked={enableReporting}
            onChange={(e, { checked }) => setEnableReporting(checked)}
          />
        </div>
      </div>
    </div>
  );

  // Step 3: Product Selection
  const renderProductSelection = () => (
    <div>
      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#080707', marginBottom: '8px' }}>
        Product Selection
      </h3>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '20px' }}>
        Choose the products to include in this order.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {wizardData.products.map((product) => {
          const isSelected = selectedProducts.includes(product.id);
          return (
            <div
              key={product.id}
              onClick={() => toggleProduct(product.id)}
              style={{
                ...cardStyle,
                padding: '16px',
                cursor: 'pointer',
                borderColor: isSelected ? '#0176d3' : '#dddbda',
                borderWidth: isSelected ? '2px' : '1px',
                backgroundColor: isSelected ? '#f0f7ff' : 'white',
                transition: 'all 0.15s ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.borderColor = '#b0c4de';
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.borderColor = isSelected ? '#0176d3' : '#dddbda';
              }}
            >
              {isSelected && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#0176d3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Icon category="utility" name="check" size="xx-small" style={{ fill: 'white' }} />
                </div>
              )}
              <div style={{ marginBottom: '8px' }}>
                <Badge
                  content={product.category}
                  style={{
                    backgroundColor: `${getCategoryColor(product.category)}15`,
                    color: getCategoryColor(product.category),
                    fontSize: '11px',
                  }}
                />
              </div>
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#080707', marginBottom: '6px' }}>
                {product.name}
              </h4>
              <p style={{ fontSize: '13px', color: '#706e6b', marginBottom: '12px', lineHeight: '1.4' }}>
                {product.description}
              </p>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#0176d3' }}>
                ${product.price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{
        ...cardStyle,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#f3f3f3',
        borderColor: selectedProducts.length > 0 ? '#0176d3' : '#dddbda',
      }}>
        <div>
          <span style={{ fontSize: '14px', color: '#706e6b' }}>
            {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
          </span>
        </div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#080707' }}>
          Total: ${totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </div>
      </div>
    </div>
  );

  // Step 4: Review
  const renderReview = () => (
    <div>
      <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#080707', marginBottom: '8px' }}>
        Review Your Order
      </h3>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '24px' }}>
        Please review all details before submitting.
      </p>

      {/* Account Summary */}
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#080707', margin: 0 }}>
            Account
          </h4>
          <Button
            label="Edit"
            variant="base"
            onClick={() => goToStep(0)}
            style={{ color: '#0176d3' }}
          />
        </div>
        {selectedAccount && (
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase' }}>Name</span>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{selectedAccount.name}</div>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase' }}>Industry</span>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{selectedAccount.industry}</div>
            </div>
            <div>
              <span style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase' }}>Type</span>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{selectedAccount.type}</div>
            </div>
          </div>
        )}
      </div>

      {/* Configuration Summary */}
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#080707', margin: 0 }}>
            Configuration
          </h4>
          <Button
            label="Edit"
            variant="base"
            onClick={() => goToStep(1)}
            style={{ color: '#0176d3' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div>
            <span style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase' }}>Type</span>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707', textTransform: 'capitalize' }}>{configType}</div>
          </div>
          <div>
            <span style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase' }}>Region</span>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>
              {selectedRegion.length > 0 ? selectedRegion[0].label : 'Not selected'}
            </div>
          </div>
          {(configType === 'custom' || configType === 'enterprise') && customDomain && (
            <div>
              <span style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase' }}>Custom Domain</span>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{customDomain}</div>
            </div>
          )}
          {(configType === 'custom' || configType === 'enterprise') && apiLimit && (
            <div>
              <span style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase' }}>API Limit</span>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>{apiLimit}/hr</div>
            </div>
          )}
        </div>
        <div style={{ marginTop: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: enableNotifications ? '#2e844a' : '#706e6b' }}>
            {enableNotifications ? '+ ' : '- '}Notifications
          </span>
          <span style={{ fontSize: '13px', color: enableAutoSync ? '#2e844a' : '#706e6b' }}>
            {enableAutoSync ? '+ ' : '- '}Auto-Sync
          </span>
          <span style={{ fontSize: '13px', color: enableReporting ? '#2e844a' : '#706e6b' }}>
            {enableReporting ? '+ ' : '- '}Reporting
          </span>
        </div>
      </div>

      {/* Products Summary */}
      <div style={{ ...cardStyle, marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#080707', margin: 0 }}>
            Products ({selectedProductDetails.length})
          </h4>
          <Button
            label="Edit"
            variant="base"
            onClick={() => goToStep(2)}
            style={{ color: '#0176d3' }}
          />
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dddbda' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '700' }}>Product</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '700' }}>Category</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '700' }}>Price</th>
            </tr>
          </thead>
          <tbody>
            {selectedProductDetails.map((product) => (
              <tr key={product.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                <td style={{ padding: '10px 12px', fontSize: '14px', color: '#080707' }}>{product.name}</td>
                <td style={{ padding: '10px 12px' }}>
                  <Badge
                    content={product.category}
                    style={{
                      backgroundColor: `${getCategoryColor(product.category)}15`,
                      color: getCategoryColor(product.category),
                      fontSize: '11px',
                    }}
                  />
                </td>
                <td style={{ padding: '10px 12px', fontSize: '14px', fontWeight: '600', color: '#080707', textAlign: 'right' }}>
                  ${product.price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #dddbda' }}>
              <td colSpan="2" style={{ padding: '12px', fontSize: '15px', fontWeight: '700', color: '#080707' }}>Total</td>
              <td style={{ padding: '12px', fontSize: '18px', fontWeight: '700', color: '#0176d3', textAlign: 'right' }}>
                ${totalPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Terms */}
      <div style={{ ...cardStyle, backgroundColor: '#fffbe5', borderColor: '#fe9339' }}>
        <Checkbox
          labels={{
            label: 'I agree to the terms and conditions, and confirm the order details are correct.',
          }}
          checked={agreedToTerms}
          onChange={(e, { checked }) => setAgreedToTerms(checked)}
        />
      </div>
    </div>
  );

  // Step 5: Confirmation
  const renderConfirmation = () => (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: '#ebf7e6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
        <Icon category="utility" name="check" size="large" style={{ fill: '#2e844a' }} />
      </div>
      <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#080707', marginBottom: '12px' }}>
        Your order has been submitted!
      </h2>
      <p style={{ fontSize: '16px', color: '#706e6b', marginBottom: '8px' }}>
        Thank you for your order. You will receive a confirmation email shortly.
      </p>
      <div style={{
        display: 'inline-block',
        backgroundColor: '#f3f3f3',
        borderRadius: '8px',
        padding: '16px 32px',
        marginTop: '16px',
        marginBottom: '32px',
      }}>
        <span style={{ fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
          Order Reference
        </span>
        <span style={{ fontSize: '22px', fontWeight: '700', color: '#0176d3', fontFamily: 'monospace' }}>
          {orderRef}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
        <Button
          label="Start New Order"
          variant="brand"
          onClick={handleReset}
        />
        <Button
          label="View Order Details"
          variant="outline-brand"
          onClick={() => goToStep(3)}
        />
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderAccountLookup();
      case 1: return renderConfiguration();
      case 2: return renderProductSelection();
      case 3: return renderReview();
      case 4: return renderConfirmation();
      default: return null;
    }
  };

  return (
    <div>
      {showToast && (
        <ToastContainer>
          <Toast
            labels={{ heading: 'Order submitted successfully!' }}
            variant="success"
            onRequestClose={() => setShowToast(false)}
          />
        </ToastContainer>
      )}

      {/* Progress Indicator */}
      <div style={{ ...cardStyle, marginBottom: '24px' }}>
        <ProgressIndicator
          steps={steps}
          selectedStep={steps[currentStep]}
          completedSteps={steps.filter((s) => s.id < currentStep)}
          onStepClick={(e, { step }) => goToStep(step.id)}
        />
      </div>

      {/* Step Content */}
      <div style={{ ...cardStyle, marginBottom: '24px', minHeight: '400px' }}>
        {renderStepContent()}
      </div>

      {/* Navigation */}
      {currentStep < 4 && (
        <div style={{
          ...cardStyle,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Button
            label="Back"
            variant="neutral"
            onClick={handleBack}
            disabled={currentStep === 0}
            iconCategory="utility"
            iconName="back"
            iconPosition="left"
          />
          <span style={{ fontSize: '13px', color: '#706e6b' }}>
            Step {currentStep + 1} of {steps.length}
          </span>
          <Button
            label={currentStep === 3 ? 'Submit Order' : 'Next'}
            variant="brand"
            onClick={handleNext}
            disabled={!canProceed()}
            iconCategory="utility"
            iconName={currentStep === 3 ? 'check' : 'forward'}
            iconPosition="right"
          />
        </div>
      )}
    </div>
  );
};


// ============================================================
// Dynamic Form Builder Tab
// ============================================================
const DynamicFormBuilder = () => {
  // Stacked form state
  const [stackedForm, setStackedForm] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
  });
  const [stackedTouched, setStackedTouched] = useState({});
  const [stackedErrors, setStackedErrors] = useState({});

  // Horizontal form state
  const [horizontalForm, setHorizontalForm] = useState({
    company: '',
    title: '',
    department: '',
    location: '',
  });

  // Compound fields state
  const [addressForm, setAddressForm] = useState({
    street: '',
    city: '',
    state: [],
    zip: '',
  });
  const [nameForm, setNameForm] = useState({
    salutation: [],
    firstName: '',
    lastName: '',
  });

  // Dynamic rows state
  const [lineItems, setLineItems] = useState([
    { id: 1, name: '', quantity: '', price: '' },
    { id: 2, name: '', quantity: '', price: '' },
    { id: 3, name: '', quantity: '', price: '' },
  ]);
  const [nextItemId, setNextItemId] = useState(4);

  // Stacked form validation
  const validateStackedField = (field, value) => {
    const errors = { ...stackedErrors };
    if (field === 'name') {
      if (!value.trim()) {
        errors.name = 'Name is required';
      } else {
        delete errors.name;
      }
    }
    if (field === 'email') {
      if (!value.trim()) {
        errors.email = 'Email is required';
      } else if (!value.includes('@')) {
        errors.email = 'Please enter a valid email address (must contain @)';
      } else {
        delete errors.email;
      }
    }
    if (field === 'phone') {
      if (value && !/^[\d\s\-()+ ]*$/.test(value)) {
        errors.phone = 'Please enter a valid phone number';
      } else {
        delete errors.phone;
      }
    }
    setStackedErrors(errors);
  };

  const handleStackedChange = (field, value) => {
    setStackedForm((prev) => ({ ...prev, [field]: value }));
    if (stackedTouched[field]) {
      validateStackedField(field, value);
    }
  };

  const handleStackedBlur = (field) => {
    setStackedTouched((prev) => ({ ...prev, [field]: true }));
    validateStackedField(field, stackedForm[field]);
  };

  // Dynamic rows handlers
  const addLineItem = () => {
    setLineItems((prev) => [...prev, { id: nextItemId, name: '', quantity: '', price: '' }]);
    setNextItemId((prev) => prev + 1);
  };

  const removeLineItem = (id) => {
    if (lineItems.length <= 1) return;
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateLineItem = (id, field, value) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const getLineTotal = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const prc = parseFloat(item.price) || 0;
    return qty * prc;
  };

  const grandTotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + getLineTotal(item), 0);
  }, [lineItems]);

  // Horizontal field row helper
  const HorizontalField = ({ label, children }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
      <label style={{
        width: '33%',
        minWidth: '120px',
        paddingTop: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#080707',
        textAlign: 'right',
        paddingRight: '16px',
      }}>
        {label}
      </label>
      <div style={{ flex: 1 }}>
        {children}
      </div>
    </div>
  );

  // Section 1: Stacked Form
  const renderStackedForm = () => (
    <div>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '20px' }}>
        Standard vertical form layout with real-time validation on blur.
      </p>
      <div style={{ maxWidth: '560px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Input
            label="Full Name"
            required
            placeholder="Enter your full name"
            value={stackedForm.name}
            onChange={(e, { value }) => handleStackedChange('name', value)}
            onBlur={() => handleStackedBlur('name')}
            errorText={stackedTouched.name ? stackedErrors.name : undefined}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Input
            label="Email Address"
            required
            placeholder="you@example.com"
            value={stackedForm.email}
            onChange={(e, { value }) => handleStackedChange('email', value)}
            onBlur={() => handleStackedBlur('email')}
            errorText={stackedTouched.email ? stackedErrors.email : undefined}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Input
            label="Phone Number"
            placeholder="(555) 123-4567"
            value={stackedForm.phone}
            onChange={(e, { value }) => handleStackedChange('phone', value)}
            onBlur={() => handleStackedBlur('phone')}
            errorText={stackedTouched.phone ? stackedErrors.phone : undefined}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Textarea
            label="Description"
            placeholder="Tell us about your needs..."
            value={stackedForm.description}
            onChange={(e, { value }) => handleStackedChange('description', value)}
          />
        </div>
        <div style={{
          display: 'flex',
          gap: '8px',
          paddingTop: '8px',
          borderTop: '1px solid #f3f3f3',
        }}>
          <Button label="Save" variant="brand" />
          <Button label="Cancel" variant="neutral" />
        </div>
      </div>
    </div>
  );

  // Section 2: Horizontal Form
  const renderHorizontalForm = () => (
    <div>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '20px' }}>
        Label-on-left layout with label taking 33% width and input taking 67%.
      </p>
      <div style={{ maxWidth: '640px' }}>
        <HorizontalField label="Company">
          <Input
            placeholder="Enter company name"
            value={horizontalForm.company}
            onChange={(e, { value }) => setHorizontalForm((prev) => ({ ...prev, company: value }))}
          />
        </HorizontalField>
        <HorizontalField label="Title">
          <Input
            placeholder="Enter job title"
            value={horizontalForm.title}
            onChange={(e, { value }) => setHorizontalForm((prev) => ({ ...prev, title: value }))}
          />
        </HorizontalField>
        <HorizontalField label="Department">
          <Input
            placeholder="Enter department"
            value={horizontalForm.department}
            onChange={(e, { value }) => setHorizontalForm((prev) => ({ ...prev, department: value }))}
          />
        </HorizontalField>
        <HorizontalField label="Location">
          <Input
            placeholder="Enter location"
            value={horizontalForm.location}
            onChange={(e, { value }) => setHorizontalForm((prev) => ({ ...prev, location: value }))}
          />
        </HorizontalField>
        <div style={{
          display: 'flex',
          gap: '8px',
          paddingTop: '12px',
          paddingLeft: '33%',
          borderTop: '1px solid #f3f3f3',
        }}>
          <Button label="Save" variant="brand" />
          <Button label="Cancel" variant="neutral" />
        </div>
      </div>
    </div>
  );

  // Section 3: Compound Fields
  const renderCompoundFields = () => (
    <div>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '20px' }}>
        Grouped input fields for addresses and names in a compact layout.
      </p>

      {/* Name compound */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ fontSize: '14px', fontWeight: '600', color: '#080707', display: 'block', marginBottom: '8px' }}>
          Full Name
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '120px 1fr 1fr',
          gap: '12px',
        }}>
          <Combobox
            labels={{ placeholder: 'Mr.' }}
            options={salutationOptions.map((s) => ({ ...s, value: s.id }))}
            selection={nameForm.salutation}
            onSelect={(e, data) => setNameForm((prev) => ({ ...prev, salutation: data.selection }))}
            variant="readonly"
            singleInputDisabled={false}
          />
          <Input
            placeholder="First Name"
            value={nameForm.firstName}
            onChange={(e, { value }) => setNameForm((prev) => ({ ...prev, firstName: value }))}
          />
          <Input
            placeholder="Last Name"
            value={nameForm.lastName}
            onChange={(e, { value }) => setNameForm((prev) => ({ ...prev, lastName: value }))}
          />
        </div>
      </div>

      {/* Address compound */}
      <div>
        <label style={{ fontSize: '14px', fontWeight: '600', color: '#080707', display: 'block', marginBottom: '8px' }}>
          Mailing Address
        </label>
        <div style={{ marginBottom: '12px' }}>
          <Input
            placeholder="Street Address"
            value={addressForm.street}
            onChange={(e, { value }) => setAddressForm((prev) => ({ ...prev, street: value }))}
          />
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 180px 120px',
          gap: '12px',
        }}>
          <Input
            placeholder="City"
            value={addressForm.city}
            onChange={(e, { value }) => setAddressForm((prev) => ({ ...prev, city: value }))}
          />
          <Combobox
            labels={{ placeholder: 'State' }}
            options={stateOptions.map((s) => ({ ...s, value: s.id }))}
            selection={addressForm.state}
            onSelect={(e, data) => setAddressForm((prev) => ({ ...prev, state: data.selection }))}
            variant="readonly"
            singleInputDisabled={false}
          />
          <Input
            placeholder="Zip Code"
            value={addressForm.zip}
            onChange={(e, { value }) => setAddressForm((prev) => ({ ...prev, zip: value }))}
          />
        </div>
      </div>
    </div>
  );

  // Section 4: Dynamic Rows
  const renderDynamicRows = () => (
    <div>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '20px' }}>
        Add and remove line items dynamically. Totals auto-calculate.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dddbda' }}>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '700', width: '35%' }}>Item Name</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '700', width: '15%' }}>Quantity</th>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '700', width: '20%' }}>Unit Price</th>
              <th style={{ textAlign: 'right', padding: '10px 12px', fontSize: '12px', color: '#706e6b', textTransform: 'uppercase', fontWeight: '700', width: '20%' }}>Total</th>
              <th style={{ textAlign: 'center', padding: '10px 12px', width: '10%' }}></th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item) => {
              const lineTotal = getLineTotal(item);
              return (
                <tr key={item.id} style={{ borderBottom: '1px solid #f3f3f3' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <Input
                      placeholder="Enter item name"
                      value={item.name}
                      onChange={(e, { value }) => updateLineItem(item.id, 'name', value)}
                    />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Input
                      placeholder="0"
                      type="number"
                      value={item.quantity}
                      onChange={(e, { value }) => updateLineItem(item.id, 'quantity', value)}
                    />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Input
                      placeholder="0.00"
                      type="number"
                      value={item.price}
                      onChange={(e, { value }) => updateLineItem(item.id, 'price', value)}
                    />
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#080707' }}>
                    ${lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <Button
                      assistiveText={{ icon: 'Remove row' }}
                      iconCategory="utility"
                      iconName="close"
                      iconSize="small"
                      variant="icon"
                      onClick={() => removeLineItem(item.id)}
                      disabled={lineItems.length <= 1}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #dddbda' }}>
              <td colSpan="3" style={{ padding: '12px', fontSize: '15px', fontWeight: '700', color: '#080707' }}>
                Grand Total
              </td>
              <td style={{ padding: '12px', fontSize: '18px', fontWeight: '700', color: '#0176d3', textAlign: 'right' }}>
                ${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{ marginTop: '16px' }}>
        <Button
          label="Add Row"
          variant="neutral"
          iconCategory="utility"
          iconName="add"
          iconPosition="left"
          onClick={addLineItem}
        />
      </div>
    </div>
  );

  // Section 5: File Upload Zone
  const renderFileUploadZone = () => (
    <div>
      <p style={{ fontSize: '14px', color: '#706e6b', marginBottom: '20px' }}>
        Drag-and-drop upload area with a list of uploaded files.
      </p>

      {/* Drop zone */}
      <div
        style={{
          border: '2px dashed #dddbda',
          borderRadius: '8px',
          padding: '48px 24px',
          textAlign: 'center',
          backgroundColor: '#fafaf9',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          marginBottom: '24px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#0176d3';
          e.currentTarget.style.backgroundColor = '#f0f7ff';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#dddbda';
          e.currentTarget.style.backgroundColor = '#fafaf9';
        }}
      >
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: '#eef4ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
        }}>
          <Icon category="utility" name="upload" size="medium" style={{ fill: '#0176d3' }} />
        </div>
        <p style={{ fontSize: '16px', fontWeight: '600', color: '#080707', marginBottom: '4px' }}>
          Drag files here or click to browse
        </p>
        <p style={{ fontSize: '13px', color: '#706e6b', margin: 0 }}>
          Supports PDF, DOCX, XLSX, PNG, JPG (max 25MB per file)
        </p>
      </div>

      {/* Uploaded files list */}
      <div>
        <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#080707', marginBottom: '12px' }}>
          Uploaded Files ({mockUploadedFiles.length})
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {mockUploadedFiles.map((file, idx) => {
            const getFileIcon = (type) => {
              switch (type) {
                case 'pdf': return 'pdf';
                case 'doc': return 'file';
                case 'image': return 'image';
                case 'spreadsheet': return 'file';
                default: return 'file';
              }
            };
            const getFileColor = (type) => {
              switch (type) {
                case 'pdf': return '#c23934';
                case 'doc': return '#0176d3';
                case 'image': return '#2e844a';
                case 'spreadsheet': return '#2e844a';
                default: return '#706e6b';
              }
            };
            return (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  backgroundColor: '#fafaf9',
                  border: '1px solid #f3f3f3',
                  borderRadius: '4px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '4px',
                    backgroundColor: `${getFileColor(file.type)}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Icon
                      category="doctype"
                      name={getFileIcon(file.type)}
                      size="small"
                      style={{ fill: getFileColor(file.type) }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#080707' }}>
                      {file.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#706e6b' }}>
                      {file.size} -- Uploaded {file.date}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <Button
                    assistiveText={{ icon: 'Download' }}
                    iconCategory="utility"
                    iconName="download"
                    iconSize="small"
                    variant="icon"
                  />
                  <Button
                    assistiveText={{ icon: 'Delete' }}
                    iconCategory="utility"
                    iconName="delete"
                    iconSize="small"
                    variant="icon"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <ExpandableSection title="Stacked Form Layout" isOpen>
        <div style={{ ...cardStyle, ...sectionSpacing }}>
          {renderStackedForm()}
        </div>
      </ExpandableSection>

      <ExpandableSection title="Horizontal Form Layout" isOpen>
        <div style={{ ...cardStyle, ...sectionSpacing }}>
          {renderHorizontalForm()}
        </div>
      </ExpandableSection>

      <ExpandableSection title="Compound Fields" isOpen>
        <div style={{ ...cardStyle, ...sectionSpacing }}>
          {renderCompoundFields()}
        </div>
      </ExpandableSection>

      <ExpandableSection title="Dynamic Line Items" isOpen>
        <div style={{ ...cardStyle, ...sectionSpacing }}>
          {renderDynamicRows()}
        </div>
      </ExpandableSection>

      <ExpandableSection title="File Upload Zone" isOpen>
        <div style={{ ...cardStyle, ...sectionSpacing }}>
          {renderFileUploadZone()}
        </div>
      </ExpandableSection>
    </div>
  );
};


// ============================================================
// Main Component
// ============================================================
const InteractiveFormsWizard = () => {
  return (
    <ShowcasePageWrapper
      title="Forms & Wizards"
      description="Multi-step wizard and dynamic form patterns"
      icon="form"
      iconCategory="standard"
    >
      <Tabs id="forms-wizard-tabs">
        <TabsPanel label="Multi-Step Wizard">
          <div style={{ paddingTop: '20px' }}>
            <MultiStepWizard />
          </div>
        </TabsPanel>
        <TabsPanel label="Dynamic Form Builder">
          <div style={{ paddingTop: '20px' }}>
            <DynamicFormBuilder />
          </div>
        </TabsPanel>
      </Tabs>
    </ShowcasePageWrapper>
  );
};

export default InteractiveFormsWizard;
